import React, { useContext, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  IconButton,
  useTheme,
  Switch,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import PaymentsIcon from '@mui/icons-material/Payments';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import { ThemeContext } from '../contexts/ThemeContext';
import { trackEvent } from './GoogleAnalytics';

const Navbar = () => {
  const theme = useTheme();
  const colorMode = useContext(ThemeContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    trackEvent('Navigation', 'Click', path);
  };

  const menuItems = [
    { text: 'Home', path: '/', icon: <HomeIcon /> },
    { text: 'Faculty', path: '/faculty', icon: <PeopleIcon /> },
    { text: 'Services', path: '/services', icon: <SchoolIcon /> },
    { text: 'Fee Structure', path: '/fee-structure', icon: <PaymentsIcon /> },
    { text: 'Contact', path: '/contact', icon: <ContactPhoneIcon /> },
  ];

  const menuButtonStyles = {
    position: 'relative',
    mx: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    '&::after': {
      content: '""',
      position: 'absolute',
      width: '0%',
      height: '2px',
      bottom: 0,
      left: '50%',
      backgroundColor: 'currentColor',
      transition: 'all 0.3s ease-in-out',
      transform: 'translateX(-50%)',
    },
    '&:hover::after': {
      width: '100%',
    },
    '&:hover': {
      backgroundColor: 'transparent',
      transform: 'translateY(-2px)',
      transition: 'transform 0.3s ease-in-out',
    },
    '& .MuiSvgIcon-root': {
      transition: 'transform 0.3s ease-in-out',
    },
    '&:hover .MuiSvgIcon-root': {
      transform: 'scale(1.2)',
    },
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <List>
        {menuItems.map((item) => (
          <ListItem 
            key={item.text} 
            component={RouterLink} 
            to={item.path}
            sx={{ 
              color: 'inherit',
              position: 'relative',
              overflow: 'hidden',
              '&::after': {
                content: '""',
                position: 'absolute',
                width: '100%',
                height: '2px',
                bottom: 0,
                left: '-100%',
                backgroundColor: 'currentColor',
                transition: 'left 0.3s ease-in-out',
              },
              '&:hover::after': {
                left: '0',
              },
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                transform: 'translateX(10px)',
                transition: 'all 0.3s ease-in-out',
              },
            }}
          >
            <ListItemIcon sx={{ 
              color: 'inherit',
              minWidth: '40px',
              '& .MuiSvgIcon-root': {
                transition: 'transform 0.3s ease-in-out',
              },
              '&:hover .MuiSvgIcon-root': {
                transform: 'scale(1.2)',
              },
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text} 
              sx={{
                textAlign: 'left',
                '& .MuiTypography-root': {
                  transition: 'all 0.3s ease-in-out',
                },
                '&:hover .MuiTypography-root': {
                  transform: 'scale(1.1)',
                },
              }}
            />
          </ListItem>
        ))}
      </List>
      <Box sx={{ borderTop: '1px solid #eee', py: 2, mt: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 'bold', letterSpacing: 1 }}>
          SOLID CLASSES
        </Typography>
      </Box>
    </Box>
  );

  return (
    <AppBar position="static">
      <Container maxWidth="lg">
        <Toolbar>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <RouterLink to="/" onClick={() => handleNavigation('/')}>
              <img
                src="/logo.png"
                alt="SOLID CLASSES"
                style={{ 
                  height: '40px', 
                  marginRight: '10px',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  }
                }}
              />
            </RouterLink>
          </Box>

          {isMobile ? (
            <>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ 
                  mr: 2,
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'rotate(90deg)',
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
              <Drawer
                variant="temporary"
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                  keepMounted: true,
                }}
                sx={{
                  display: { xs: 'block', md: 'none' },
                  '& .MuiDrawer-paper': { 
                    boxSizing: 'border-box', 
                    width: 240,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                  },
                }}
              >
                {drawer}
              </Drawer>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  color="inherit"
                  component={RouterLink}
                  to={item.path}
                  onClick={() => handleNavigation(item.path)}
                  sx={menuButtonStyles}
                  startIcon={item.icon}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
            <IconButton 
              sx={{ 
                mr: 1,
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'rotate(180deg)',
                }
              }} 
              onClick={colorMode.toggleColorMode} 
              color="inherit"
            >
              {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            <Switch
              checked={theme.palette.mode === 'dark'}
              onChange={colorMode.toggleColorMode}
              inputProps={{ 'aria-label': 'theme switch' }}
              color="default"
            />
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar; 
